import { useEffect, useState, useMemo } from "react";
import { Calculator, Users, Info } from "lucide-react";
import api from "@/lib/api";

const WEEKS_PER_MONTH = 4.33;

const fmt = (v) => {
  try {
    return `${Number(v).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  } catch {
    return "—";
  }
};

export default function QuickEstimateSimulator() {
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [days, setDays] = useState(5);
  const [hours, setHours] = useState(8);
  const [beneficiaries, setBeneficiaries] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/pricing/config")
      .then((r) => {
        const active = (r.data?.rows || []).filter((x) => x.active);
        setRows(active);
        // Prefer the SEMANA row as default
        const semana = active.find((x) => (x.name || "").toUpperCase().includes("SEMANA") && !((x.name || "").toUpperCase().includes("FIM"))) || active[0];
        if (semana) {
          setSelectedId(semana.id || semana.name);
          setDays(semana.days_per_week || 5);
          setHours(semana.hours_per_day || 8);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const selected = useMemo(
    () => rows.find((r) => (r.id || r.name) === selectedId) || null,
    [rows, selectedId]
  );
  const hourly = Number(selected?.hourly_price || 0);

  const weeklyHours = Number(days) * Number(hours);
  const monthlyHours = weeklyHours * WEEKS_PER_MONTH;
  const weeklyValue = hourly * weeklyHours;
  const monthlyAvg = weeklyValue * WEEKS_PER_MONTH;

  const pickRow = (r) => {
    setSelectedId(r.id || r.name);
    setDays(r.days_per_week || 5);
    setHours(r.hours_per_day || 8);
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 mb-8" data-testid="quick-simulator-loading">
        <div className="h-6 w-40 bg-muted rounded animate-pulse mb-3" />
        <div className="h-24 bg-muted/40 rounded animate-pulse" />
      </div>
    );
  }
  if (rows.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 mb-8" data-testid="quick-simulator">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
            <Calculator className="w-4 h-4 text-foreground" />
          </div>
          <div>
            <h3 className="font-ui text-lg text-foreground">Simulador rápido</h3>
            <div className="text-xs text-muted-foreground">Estime valores sem criar lead — atalho para responder no telefone</div>
          </div>
        </div>
        <div className="inline-flex bg-muted border border-border rounded-full p-1" data-testid="sim-beneficiary-toggle">
          {[1, 2].map((n) => (
            <button
              key={n}
              onClick={() => setBeneficiaries(n)}
              data-testid={`sim-b-${n}`}
              className={`text-xs px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${
                beneficiaries === n
                  ? "bg-card text-foreground shadow-sm font-semibold"
                  : "text-muted-foreground"
              }`}>
              <Users className="w-3 h-3" /> {n} pessoa{n > 1 ? "s" : ""}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1.2fr] gap-6">
        <div className="space-y-4">
          {/* Modality pills */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold mb-2">Modalidade</div>
            <div className="flex flex-wrap gap-2" data-testid="sim-modalities">
              {rows.map((r) => {
                const rid = r.id || r.name;
                const active = rid === selectedId;
                return (
                  <button
                    key={rid}
                    onClick={() => pickRow(r)}
                    data-testid={`sim-mod-${(r.name||'').toLowerCase().replace(/\s+/g,'-')}`}
                    className={`text-xs px-3.5 py-2 rounded-full border transition-colors ${
                      active
                        ? "bg-foreground text-background border-[#0F172A]"
                        : "bg-card text-[#374151] border-border hover:border-primary"
                    }`}>
                    {r.name} · {fmt(r.hourly_price)}/h
                  </button>
                );
              })}
            </div>
          </div>

          {/* Inputs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold mb-1">Dias/semana</label>
              <input
                type="number" min="1" max="7" step="1"
                value={days}
                onChange={(e) => setDays(Math.max(1, Math.min(7, Number(e.target.value) || 0)))}
                data-testid="sim-days"
                className="w-full h-11 bg-muted/40 border border-border rounded-full px-4 text-sm text-foreground focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold mb-1">Horas/dia</label>
              <input
                type="number" min="1" max="24" step="0.5"
                value={hours}
                onChange={(e) => setHours(Math.max(0.5, Math.min(24, Number(e.target.value) || 0)))}
                data-testid="sim-hours"
                className="w-full h-11 bg-muted/40 border border-border rounded-full px-4 text-sm text-foreground focus:outline-none focus:border-primary" />
            </div>
          </div>

          {beneficiaries === 2 && (
            <div className="bg-muted/40 border border-primary/40 rounded-lg px-3 py-2 flex items-start gap-2 text-[12px] text-muted-foreground" data-testid="sim-b2-note">
              <Info className="w-3.5 h-3.5 text-foreground mt-0.5 flex-shrink-0" />
              <span>
                Para 2 pessoas na mesma habitação, o serviço é <b>partilhado pela mesma Ajudante</b>.
                Confirme com o gestor eventual ajuste (surcharge conforme complexidade clínica).
              </span>
            </div>
          )}
        </div>

        {/* Output */}
        <div className="bg-muted/40 border border-primary/40 rounded-xl p-5 flex flex-col justify-center" data-testid="sim-output">
          <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold">
            Estimativa mensal · {selected?.name || "—"}
          </div>
          <div className="font-display text-4xl lg:text-5xl tracking-tight text-foreground mt-2" data-testid="sim-monthly">
            {fmt(monthlyAvg)}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4 text-[12px]">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Semana</div>
              <div className="font-ui text-sm text-foreground" data-testid="sim-weekly">
                {fmt(weeklyValue)} · {weeklyHours.toFixed(1)}h
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Total horas/mês</div>
              <div className="font-ui text-sm text-foreground">{monthlyHours.toFixed(1)}h</div>
            </div>
          </div>
          <div className="text-[10px] text-muted-foreground/70 mt-4 border-t border-primary/30 pt-2">
            Cálculo indicativo · base {fmt(hourly)}/h · 4,33 semanas/mês
          </div>
        </div>
      </div>
    </div>
  );
}
